<template>
    <div>
        <PageHeader>
            {{ $t('conversionReport') }}
            <template #side>
                <div class="d-flex gap-4">
                    <Multiselect
                        v-model="sources"
                        :options="requestSources.data || []"
                        :placeholder="$t('source')"
                        multiple
                        show-checkboxes
                        :close-on-select="false"
                        class="width-min-250"
                        :loading="requestSources.loading"
                        @change="fetchData()"
                    />
                    <TimeRangeCalendar v-model="timeRange" @timeRange="fetchData()" />
                </div>
            </template>
        </PageHeader>

        <div class="row">
            <div class="col-xl-8 mb-3">
                <MultiEChartCard
                    :data="chartsData"
                    :height="420"
                    :loading="isChartOverviewLoading"
                    :title="$t('dashboard.overview')"
                    nav-tabs
                    :range="groupBy"
                    type="time"
                    @currentChart="setCurrentChart"
                />
            </div>

            <div class="col-xl-4 mb-3">
                <div class="card mb-0">
                    <div class="card-body">
                        <h4 class="card-title header-title">{{ $t('conversionFunnel') }}</h4>
                        <div v-for="key in Object.keys(chartOverview)" :key="key" class="d-flex gap-3 border-bottom justify-content-between align-items-end pt-2 pb-3">
                            <div class="flex-grow-1 flex-basis-0">
                                <h6 class="text-muted mt-0 fs-14">{{ chartOverview[key].name }}</h6>
                                <h3 class="my-2">
                                    <b-skeleton v-if="getLoading(key)" animation="wave" width="100%" />
                                    <div v-else>{{ chartOverview[key].value }}</div>
                                </h3>
                                <h6
                                    class="mb-0"
                                    :class="{
                                        'text-success': isPositive(chartOverview[key].difference),
                                        'text-danger': !isPositive(chartOverview[key].difference),
                                    }"
                                >
                                    <b-skeleton v-if="getLoading(key)" animation="wave" width="80%" />
                                    <div v-else>
                                        {{ getSign(chartOverview[key].difference) }}
                                        {{ $format.percent(Math.abs(chartOverview[key].difference)) }}
                                    </div>
                                </h6>
                            </div>
                            <div class="flex-grow-1 flex-basis-0 text-center">
                                <b-spinner v-if="getLoading(key)" />
                                <EChart v-else type="time" class="w-100" :height="45" :options="chartOverview[key].miniOption" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <DataTable
            :header="$t('publishersList')"
            :error="getError(currentChart)"
            :items="getPublishers(currentChart)"
            :total-rows="getPublishers(currentChart).length"
            :loading="getLoading(currentChart)"
            :fields="fields"
        >
            <template #cell(createdAt)="{ value }">
                {{ dateFormat(value, 'yyyy-MM-dd HH:mm') }}
            </template>
            <template #cell(action)="data">
                <div class="d-flex gap-1 justify-content-end align-items-start">
                    <ButtonIcon size="sm" variant="warning" :disabled="isItemBusy(data.item.id)" :loading="isItemUpdated(data.item.id)" :to="createItemUrl(data.item)">
                        {{ $t('details') }}
                    </ButtonIcon>
                    <ButtonIcon size="sm" variant="secondary" :disabled="isItemBusy(data.item.id)" :loading="isTokenLoading(data.item.id)" @click="loginAs(data.item)">
                        {{ $t('go2') }}
                    </ButtonIcon>
                </div>
            </template>
        </DataTable>
    </div>
</template>

<script>
    import { format as dateFormat } from 'date-fns';
    import { generateEChartData, chartDataGroupBy } from '@/utils/chart';
    import { calculateChangePercentage } from '@/utils/number';
    import { stringToColour } from '@/utils/util';
    import { createItemKey } from '@/store/modules/moduleFactory';

    import PageHeader from '@/components/common/PageHeader.vue';
    import TimeRangeCalendar from '@/components/common/TimeRangeCalendar.vue';
    import MultiEChartCard from '@/components/common/MultiEChartCard.vue';
    import DataTable from '@/components/common/DataTable.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import EChart from '@/components/common/EChart.vue';

    export default {
        name: 'ReportsConversion',
        components: {
            PageHeader,
            TimeRangeCalendar,
            MultiEChartCard,
            DataTable,
            Multiselect,
            EChart,
        },
        data() {
            return {
                requestSources: this.$req('conversionStats/UniqueSources'),
                requestRegistrationStats: this.$req('conversionStats/RegistrationStats'),
                requestSpaceStats: this.$req('conversionStats/SpaceStats'),
                requestClickStats: this.$req('conversionStats/ClickStats'),
                requestTransactionStats: this.$req('conversionStats/TransactionStats'),
                requestTipTransactionStats: this.$req('conversionStats/TipTransactionStats'),
                timeRange: null,
                sources: [],
                currentChart: 'registration',
                dateFormat,
            };
        },
        computed: {
            requests() {
                return {
                    registration: this.requestRegistrationStats,
                    space: this.requestSpaceStats,
                    click: this.requestClickStats,
                    transaction: this.requestTransactionStats,
                    tipTransaction: this.requestTipTransactionStats,
                };
            },
            requestParams() {
                return { ...this.timeRange, sources: this.sources };
            },
            groupBy() {
                return chartDataGroupBy(this.timeRange);
            },
            chartOverview() {
                const overview = {};
                for (const key of Object.keys(this.requests)) {
                    const data = this.requests[key].data || {};
                    const raw = (data.chart || []).map(item => [item.date, item.users]);
                    const seriesData = generateEChartData(this.timeRange, raw);

                    const color = stringToColour(`color-${key}`);
                    const name = this.$t(`conversionReportChartName.${key}`);
                    overview[key] = {
                        name,
                        value: data.value,
                        valueCompare: data.value_compare,
                        difference: calculateChangePercentage(data.value, data.value_compare),
                        miniOption: this.generateChartOption(seriesData, name, color, true),
                        option: this.generateChartOption(seriesData, name, color, false),
                    };
                }
                return overview;
            },
            chartsData() {
                return Object.keys(this.requests).reduce((acc, key) => ({ ...acc, [key]: this.chartOverview[key].option }), {});
            },
            isChartOverviewLoading() {
                return this.getLoading(this.currentChart);
            },
            fields() {
                return [
                    { key: 'id', label: this.$t('id'), sortable: true },
                    { key: 'createdAt', label: this.$t('dateAdded'), sortable: true },
                    { key: 'email', label: this.$t('emailAddress'), sortable: true },
                    { key: 'name', label: this.$t('name'), sortable: true },
                    { key: 'source', label: this.$t('source'), sortable: true },
                    { key: 'action', label: '', thClass: 'text-right' },
                ];
            },
            isTokenLoading() {
                return id => this.$store.getters['user/getBusyItem']('userToken', createItemKey({ id }));
            },
            isItemBusy() {
                return id => ['users', 'publishers', 'producers', 'reporters'].some(type => this.$store.getters['user/getBusyItem'](type, createItemKey({ id })));
            },
            isItemUpdated() {
                return id => ['users', 'publishers', 'producers', 'reporters'].some(type => this.$store.getters['user/getUpdatedItem'](type, createItemKey({ id })));
            },
        },
        watch: {
            timeRange() {
                this.sources = [];
                this.fetchSources();
            },
        },
        methods: {
            generateChartOption(data, name, color, minimal = false) {
                return {
                    name,
                    color: [color],
                    xAxis: {
                        boundaryGap: false,
                        show: !minimal,
                    },
                    yAxis: {
                        type: 'value',
                        show: !minimal,
                        splitLine: { show: !minimal },
                    },
                    grid: minimal ? { left: 0, right: 0, top: 0, bottom: 0, containLabel: false } : undefined,
                    tooltip: minimal
                        ? {
                              show: true,
                              trigger: 'axis',
                              valueFormatter: val => val,
                          }
                        : { trigger: 'axis' },
                    legend: { show: false },
                    series: [
                        {
                            name,
                            type: 'line',
                            smooth: true,
                            symbol: 'none',
                            data,
                            lineStyle: { color },
                            itemStyle: { color },
                        },
                    ],
                };
            },
            fetchSources() {
                this.requestSources.reset();
                if (!this.timeRange) return Promise.resolve([]);
                const { startDate, endDate } = this.timeRange;
                return this.requestSources.get({ requestParams: { startDate, endDate } });
            },
            getLoading(type) {
                return this.requests[type]?.loading || false;
            },
            getError(type) {
                return this.requests[type]?.error;
            },
            getPublishers(type) {
                return this.requests[type]?.data?.publishers || [];
            },
            fetchData() {
                const params = { requestParams: this.requestParams };
                Object.values(this.requests).forEach(req => req.reset().get(params));
            },
            setCurrentChart(chart) {
                this.currentChart = chart;
            },
            getSign(val) {
                const sign = Math.sign(val);
                if (sign === 1) return '+';
                if (sign === -1) return '-';
                return '';
            },
            isPositive(val) {
                const sign = Math.sign(val);
                if (sign === 1) return true;
                if (sign === -1) return false;
                return null;
            },
            createItemUrl({ id }) {
                return {
                    name: 'UsersDetails',
                    params: { id, parentRoute: this.$route },
                };
            },
            async loginAs(user) {
                return this.$store.dispatch('auth/loginAs', { ...user, role: 'ROLE_WIDGETER' }).catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
        },
    };
</script>
